/**
 * 後工程 (invoices / purchase_orders / deliveries / sold_devices) の hooks。
 * 仕様書: docs/01_spec/sales_implementation_prompt_v3_1.md §6, §10
 *
 * - 一覧読込: collectionGroup で全 deal 横断取得
 * - 状態遷移は Firestore 直接 update (rules で editor+ 許可)
 * - 譲渡は transferSoldDevice Callable
 */
import type {
  BillingAddress,
  Delivery,
  DeliveryStatus,
  Invoice,
  InvoiceStatus,
  PublicInvoiceView,
  PurchaseOrder,
  PurchaseOrderInspection,
  PurchaseOrderStatus,
  SoldDevice,
} from '@goenhub/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  collection,
  collectionGroup,
  doc,
  getDocs,
  orderBy,
  query,
  serverTimestamp,
  Timestamp,
  updateDoc,
  where,
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';

import { db, functions } from '@/lib/firebase';

// ─── invoices ───
export interface InvoiceRecord extends Invoice {
  id: string;
}

export function useInvoices() {
  return useQuery({
    queryKey: ['invoices', 'all'],
    queryFn: async (): Promise<InvoiceRecord[]> => {
      const snap = await getDocs(
        query(collectionGroup(db, 'invoices'), orderBy('issuedAt', 'desc')),
      );
      return snap.docs.map((d) => ({ id: d.id, ...(d.data() as Invoice) }));
    },
  });
}

export function useDealInvoices(dealId: string | null | undefined) {
  return useQuery({
    queryKey: ['invoices', 'deal', dealId],
    enabled: !!dealId,
    queryFn: async (): Promise<InvoiceRecord[]> => {
      if (!dealId) return [];
      const snap = await getDocs(
        query(collection(db, 'deals', dealId, 'invoices'), orderBy('issuedAt', 'desc')),
      );
      return snap.docs.map((d) => ({ id: d.id, ...(d.data() as Invoice) }));
    },
  });
}

// 公開請求書取得 (認証不要)
const getPublicInvoiceFn = httpsCallable<
  { token: string },
  { status: 'ok'; invoice: PublicInvoiceView } | { status: 'expired' } | { status: 'not_found' }
>(functions, 'getPublicInvoice');

export function usePublicInvoice(token: string | null) {
  return useQuery({
    queryKey: ['public_invoice', token],
    enabled: !!token,
    queryFn: async () => {
      if (!token) return null;
      const r = await getPublicInvoiceFn({ token });
      return r.data;
    },
  });
}

/**
 * 請求書の発行日 / 支払期限を編集する。
 * 自動起票後に運用都合（バックデート発行・支払猶予の調整など）で書き換える用途。
 */
export function useUpdateInvoiceDates() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      dealId: string;
      invoiceId: string;
      issuedAt?: Date | null;
      dueDate?: Date | null;
    }) => {
      const ref = doc(db, 'deals', input.dealId, 'invoices', input.invoiceId);
      const update: Record<string, unknown> = {
        updatedAt: serverTimestamp(),
      };
      if (input.issuedAt !== undefined) {
        update.issuedAt = input.issuedAt ? Timestamp.fromDate(input.issuedAt) : null;
      }
      if (input.dueDate !== undefined) {
        update.dueDate = input.dueDate ? Timestamp.fromDate(input.dueDate) : null;
      }
      await updateDoc(ref, update);
      return input;
    },
    onSuccess: (vars) => {
      qc.invalidateQueries({ queryKey: ['invoices'] });
      qc.invalidateQueries({ queryKey: ['invoices', 'deal', vars.dealId] });
      qc.invalidateQueries({ queryKey: ['deals', 'detail', vars.dealId] });
    },
  });
}

/**
 * 請求書の件名 / 請求先住所スナップショットを編集（rules で editor+ 許可の直接 update）。
 * billingAddressSnapshot は発行時の固定値だが、誤りや宛先変更に対応するため手動編集を許容する。
 */
export function useUpdateInvoiceMeta() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      dealId: string;
      invoiceId: string;
      title?: string | null;
      billingAddressSnapshot?: BillingAddress | null;
    }) => {
      const ref = doc(db, 'deals', input.dealId, 'invoices', input.invoiceId);
      const update: Record<string, unknown> = {
        updatedAt: serverTimestamp(),
      };
      if (input.title !== undefined) update.title = input.title;
      if (input.billingAddressSnapshot !== undefined) {
        update.billingAddressSnapshot = input.billingAddressSnapshot;
      }
      await updateDoc(ref, update);
      return input;
    },
    onSuccess: (vars) => {
      qc.invalidateQueries({ queryKey: ['invoices'] });
      qc.invalidateQueries({ queryKey: ['invoices', 'deal', vars.dealId] });
      qc.invalidateQueries({ queryKey: ['deals', 'detail', vars.dealId] });
    },
  });
}

export function useUpdateInvoiceStatus() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      dealId: string;
      invoiceId: string;
      status: InvoiceStatus;
      paymentMethod?: 'transfer' | 'credit_card' | 'other' | null;
    }) => {
      const ref = doc(db, 'deals', input.dealId, 'invoices', input.invoiceId);
      const update: Record<string, unknown> = {
        status: input.status,
        updatedAt: serverTimestamp(),
      };
      if (input.status === 'paid') {
        update.paidAt = Timestamp.now();
        if (input.paymentMethod) update.paymentMethod = input.paymentMethod;
      }
      await updateDoc(ref, update);
      return input;
    },
    onSuccess: (vars) => {
      qc.invalidateQueries({ queryKey: ['invoices'] });
      qc.invalidateQueries({ queryKey: ['invoices', 'deal', vars.dealId] });
      qc.invalidateQueries({ queryKey: ['deals', 'detail', vars.dealId] });
    },
  });
}

// ─── invoice mail (編集モーダル) ───
/** 送信元プルダウンの候補（外部SA は 1 件に制限される）。 */
export interface MailAccountOption {
  id: string;
  displayName: string;
  email: string;
}

export interface PrepareDocumentMailResult {
  defaultTo: string;
  fromAccountId: string | null;
  subject: string;
  body: string;
  publicUrl: string;
  /**
   * null / undefined = 制限なし（listMailAccounts の全件から選択）。
   * 外部SA の場合はシステムが実際に使う 1 件のみが返る。
   */
  fromAccountOptions?: MailAccountOption[] | null;
}

const prepareInvoiceMailFn = httpsCallable<
  { dealId: string; invoiceId: string },
  PrepareDocumentMailResult
>(functions, 'prepareInvoiceMail');

/** 請求メール編集モーダルの初期値を取得（送信元/件名/本文/公開URL）。 */
export function usePrepareInvoiceMail() {
  return useMutation({
    mutationFn: async (input: { dealId: string; invoiceId: string }) => {
      const r = await prepareInvoiceMailFn(input);
      return r.data;
    },
  });
}

const sendInvoiceMailFn = httpsCallable<
  {
    dealId: string;
    invoiceId: string;
    to?: string | null;
    fromAccountId?: string | null;
    cc?: string[] | null;
    subject?: string | null;
    body?: string | null;
  },
  { invoiceId: string; mailSent: boolean; reason?: string }
>(functions, 'sendInvoiceMail');

export function useSendInvoiceMail() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: Parameters<typeof sendInvoiceMailFn>[0]) => {
      const r = await sendInvoiceMailFn(input);
      return r.data;
    },
    onSuccess: (_d, vars) => {
      if (!vars) return;
      qc.invalidateQueries({ queryKey: ['invoices', 'deal', vars.dealId] });
      qc.invalidateQueries({ queryKey: ['deals', 'detail', vars.dealId] });
    },
  });
}

// ─── purchase_orders ───
export interface PurchaseOrderRecord extends PurchaseOrder {
  id: string;
}

export function usePurchaseOrders() {
  return useQuery({
    queryKey: ['purchase_orders', 'all'],
    queryFn: async (): Promise<PurchaseOrderRecord[]> => {
      const snap = await getDocs(
        query(collectionGroup(db, 'purchase_orders'), orderBy('createdAt', 'desc')),
      );
      return snap.docs.map((d) => ({ id: d.id, ...(d.data() as PurchaseOrder) }));
    },
  });
}

export function useDealPurchaseOrders(dealId: string | null | undefined) {
  return useQuery({
    queryKey: ['purchase_orders', 'deal', dealId],
    enabled: !!dealId,
    queryFn: async (): Promise<PurchaseOrderRecord[]> => {
      if (!dealId) return [];
      const snap = await getDocs(
        query(collection(db, 'deals', dealId, 'purchase_orders'), orderBy('createdAt', 'desc')),
      );
      return snap.docs.map((d) => ({ id: d.id, ...(d.data() as PurchaseOrder) }));
    },
  });
}

export function useUpdatePurchaseOrder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      dealId: string;
      poId: string;
      status?: PurchaseOrderStatus;
      supplierName?: string | null;
      supplierContact?: string | null;
      remarks?: string | null;
    }) => {
      const ref = doc(db, 'deals', input.dealId, 'purchase_orders', input.poId);
      const update: Record<string, unknown> = { updatedAt: serverTimestamp() };
      if (input.status) {
        update.status = input.status;
        if (input.status === 'ordered') update.orderedAt = Timestamp.now();
        // shipped (顧客へ発送) は useShipPurchaseOrder で配送業者・追跡番号と一緒に入れる
      }
      if ('supplierName' in input) update.supplierName = input.supplierName;
      if ('supplierContact' in input) update.supplierContact = input.supplierContact;
      if ('remarks' in input) update.remarks = input.remarks;
      await updateDoc(ref, update);
      return input;
    },
    onSuccess: (vars) => {
      qc.invalidateQueries({ queryKey: ['purchase_orders'] });
      qc.invalidateQueries({ queryKey: ['purchase_orders', 'deal', vars.dealId] });
    },
  });
}

/**
 * 入荷・検品の記録を保存し、記録から決まる状態 (発注済 / 入荷済 / 検品済) に更新する (2026-10-30〜)。
 * status は呼び出し側で statusFromInspection() で決める。
 */
export function useSavePurchaseOrderInspection() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      dealId: string;
      poId: string;
      inspection: PurchaseOrderInspection;
      status: PurchaseOrderStatus;
    }) => {
      const ref = doc(db, 'deals', input.dealId, 'purchase_orders', input.poId);
      await updateDoc(ref, {
        inspection: input.inspection,
        status: input.status,
        updatedAt: serverTimestamp(),
      });
      return input;
    },
    onSuccess: (vars) => {
      qc.invalidateQueries({ queryKey: ['purchase_orders'] });
      qc.invalidateQueries({ queryKey: ['deals', vars.dealId, 'activities'] });
    },
  });
}

/**
 * 顧客へ発送する (PO.status='shipped')。onPurchaseOrderShipped が発送の記録を作り、
 * 顧客へ出荷通知を送り、商談を「発送・納品」へ進める。
 */
export function useShipPurchaseOrder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      dealId: string;
      poId: string;
      carrierName: string;
      trackingNumber: string | null;
    }) => {
      const ref = doc(db, 'deals', input.dealId, 'purchase_orders', input.poId);
      await updateDoc(ref, {
        status: 'shipped',
        shippedAt: Timestamp.now(),
        shipCarrierName: input.carrierName,
        shipTrackingNumber: input.trackingNumber,
        updatedAt: serverTimestamp(),
      });
      return input;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['purchase_orders'] });
      // 発送の記録と段階の移動はサーバー側で少し遅れて反映されるため、数秒後にも読み直す
      const refresh = () => {
        qc.invalidateQueries({ queryKey: ['deliveries'] });
        qc.invalidateQueries({ queryKey: ['deals'] });
      };
      refresh();
      setTimeout(refresh, 4000);
      setTimeout(refresh, 10000);
    },
  });
}

// ─── deliveries ───
export interface DeliveryRecord extends Delivery {
  id: string;
}

export function useDeliveries() {
  return useQuery({
    queryKey: ['deliveries', 'all'],
    queryFn: async (): Promise<DeliveryRecord[]> => {
      const snap = await getDocs(
        query(collectionGroup(db, 'deliveries'), orderBy('createdAt', 'desc')),
      );
      return snap.docs.map((d) => ({ id: d.id, ...(d.data() as Delivery) }));
    },
  });
}

export function useDealDeliveries(dealId: string | null | undefined) {
  return useQuery({
    queryKey: ['deliveries', 'deal', dealId],
    enabled: !!dealId,
    queryFn: async (): Promise<DeliveryRecord[]> => {
      if (!dealId) return [];
      const snap = await getDocs(
        query(collection(db, 'deals', dealId, 'deliveries'), orderBy('createdAt', 'desc')),
      );
      return snap.docs.map((d) => ({ id: d.id, ...(d.data() as Delivery) }));
    },
  });
}

export function useUpdateDelivery() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      dealId: string;
      deliveryId: string;
      status?: DeliveryStatus;
      carrierName?: string | null;
      trackingNumber?: string | null;
      remarks?: string | null;
      lineItems?: Delivery['lineItems'];
    }) => {
      const ref = doc(db, 'deals', input.dealId, 'deliveries', input.deliveryId);
      const update: Record<string, unknown> = { updatedAt: serverTimestamp() };
      if (input.status) {
        update.status = input.status;
        if (input.status === 'completed') update.deliveredAt = Timestamp.now();
      }
      if ('carrierName' in input) update.carrierName = input.carrierName;
      if ('trackingNumber' in input) update.trackingNumber = input.trackingNumber;
      if ('remarks' in input) update.remarks = input.remarks;
      if (input.lineItems) update.lineItems = input.lineItems;
      await updateDoc(ref, update);
      return input;
    },
    onSuccess: (vars) => {
      qc.invalidateQueries({ queryKey: ['deliveries'] });
      qc.invalidateQueries({ queryKey: ['deliveries', 'deal', vars.dealId] });
      qc.invalidateQueries({ queryKey: ['sold_devices'] });
    },
  });
}

// ─── sold_devices ───
export interface SoldDeviceRecord extends SoldDevice {
  id: string;
}

/**
 * T4: includeAsOwner=true なら ownerContactId 一致の機器も合算する。
 * Firestore の OR クエリは collection 内 array-contains-any 系しか使えないため、
 * クライアント側で 2 クエリを統合する。
 */
export function useSoldDevices(opts?: {
  contactId?: string;
  /** T4: contactId に加えて ownerContactId 一致 (=親として所有) の機器も含める。 */
  includeAsOwner?: boolean;
}) {
  return useQuery({
    queryKey: ['sold_devices', opts ?? null],
    queryFn: async (): Promise<SoldDeviceRecord[]> => {
      if (opts?.contactId) {
        // 使用者として: contactId 一致
        const userQ = query(
          collection(db, 'sold_devices'),
          where('contactId', '==', opts.contactId),
          orderBy('installedAt', 'desc'),
        );
        const userSnap = await getDocs(userQ);
        const userDevices = userSnap.docs.map((d) => ({
          id: d.id,
          ...(d.data() as SoldDevice),
        }));
        if (!opts.includeAsOwner) return userDevices;
        // 親 (所有者) として: ownerContactId 一致 (使用者と同一は除外)
        const ownerQ = query(
          collection(db, 'sold_devices'),
          where('ownerContactId', '==', opts.contactId),
          orderBy('installedAt', 'desc'),
        );
        const ownerSnap = await getDocs(ownerQ);
        const ownerDevices = ownerSnap.docs
          .map((d) => ({ id: d.id, ...(d.data() as SoldDevice) }))
          .filter((d) => d.contactId !== opts.contactId); // 自分自身が使用者でもある行は重複
        // 結合 + installedAt 降順で再ソート
        return [...userDevices, ...ownerDevices].sort((a, b) => {
          const at =
            typeof a.installedAt === 'object' && 'toMillis' in a.installedAt
              ? a.installedAt.toMillis()
              : new Date(a.installedAt as string).getTime();
          const bt =
            typeof b.installedAt === 'object' && 'toMillis' in b.installedAt
              ? b.installedAt.toMillis()
              : new Date(b.installedAt as string).getTime();
          return bt - at;
        });
      }
      const snap = await getDocs(
        query(collection(db, 'sold_devices'), orderBy('installedAt', 'desc')),
      );
      return snap.docs.map((d) => ({ id: d.id, ...(d.data() as SoldDevice) }));
    },
  });
}

const transferSoldDeviceFn = httpsCallable<
  { soldDeviceId: string; toContactId: string; transferredAt: string; reason: string },
  { soldDeviceId: string; fromContactId: string; toContactId: string }
>(functions, 'transferSoldDevice');

export function useTransferSoldDevice() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: Parameters<typeof transferSoldDeviceFn>[0]) => {
      const r = await transferSoldDeviceFn(input);
      return r.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['sold_devices'] }),
  });
}

// ─── 旧 CRM (Excel) からの一括取込 (v3.2.X+) ───
export interface BulkImportSoldDeviceItem {
  legacyDataId: string;
  contactId: string;
  productKind: string;
  serialNumber: string;
  productName: string;
  installedAt: string;
  warrantyStartAt?: string | null;
  warrantyEndAt?: string | null;
  serviceTag?: string | null;
  expressCode?: string | null;
  remarks?: string | null;
  nesHealth?: {
    deviceKind?: string | null;
    mind?: boolean | null;
    mr?: boolean | null;
    smi?: boolean | null;
    bwsEmail?: string | null;
    shippedAt?: string | null;
    receivedAt?: string | null;
  } | null;
}

const bulkImportSoldDevicesFn = httpsCallable<
  { items: BulkImportSoldDeviceItem[] },
  {
    imported: number;
    skipped: number;
    errors: { legacyDataId: string; reason: string }[];
  }
>(functions, 'bulkImportSoldDevices');

export function useBulkImportSoldDevices() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (items: BulkImportSoldDeviceItem[]) => {
      const r = await bulkImportSoldDevicesFn({ items });
      return r.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['sold_devices'] }),
  });
}

// ─── archive / unarchive / update (v3.3.x: 詳細モーダル経由) ───
const archiveSoldDeviceFn = httpsCallable<
  { soldDeviceId: string },
  { soldDeviceId: string; decommissioned: boolean }
>(functions, 'archiveSoldDevice');

const unarchiveSoldDeviceFn = httpsCallable<
  { soldDeviceId: string },
  { soldDeviceId: string; decommissioned: boolean }
>(functions, 'unarchiveSoldDevice');

export function useArchiveSoldDevice() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (soldDeviceId: string) => {
      const r = await archiveSoldDeviceFn({ soldDeviceId });
      return r.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['sold_devices'] }),
  });
}

export function useUnarchiveSoldDevice() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (soldDeviceId: string) => {
      const r = await unarchiveSoldDeviceFn({ soldDeviceId });
      return r.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['sold_devices'] }),
  });
}

export interface UpdateSoldDevicePatch {
  serialNumber?: string;
  modelName?: string;
  variation?: string | null;
  productKind?: string | null;
  productName?: string;
  remarks?: string | null;
  installedAt?: string;
  warrantyStartAt?: string | null;
  warrantyEndAt?: string | null;
  serviceTag?: string | null;
  expressCode?: string | null;
  nesHealth?: {
    deviceKind?: string | null;
    mind?: boolean | null;
    mr?: boolean | null;
    smi?: boolean | null;
    bwsEmail?: string | null;
    shippedAt?: string | null;
    receivedAt?: string | null;
  } | null;
}

const updateSoldDeviceFn = httpsCallable<
  { soldDeviceId: string; patch: UpdateSoldDevicePatch },
  { soldDeviceId: string; updatedFields: string[] }
>(functions, 'updateSoldDevice');

export function useUpdateSoldDevice() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { soldDeviceId: string; patch: UpdateSoldDevicePatch }) => {
      const r = await updateSoldDeviceFn(input);
      return r.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['sold_devices'] }),
  });
}
